import { normalizeBaseUrl } from './config';
import { createLogger } from './logger';
export class SkyChatApi {
    baseUrl;
    token;
    fetcher;
    logger;
    constructor(options) {
        this.baseUrl = normalizeBaseUrl(options.baseUrl);
        this.token = options.token || null;
        this.fetcher = options.fetcher || globalThis.fetch.bind(globalThis);
        this.logger = options.logger || createLogger(options.debug === true);
    }
    configure(config) {
        if (config.apiBaseUrl)
            this.baseUrl = normalizeBaseUrl(config.apiBaseUrl);
        if (config.token !== undefined)
            this.token = config.token || null;
    }
    getBaseUrl() {
        return this.baseUrl;
    }
    withToken(token) {
        return new SkyChatApi({
            baseUrl: this.baseUrl,
            token,
            fetcher: this.fetcher
        });
    }
    setToken(token) {
        this.token = token;
    }
    absolute(path) {
        if (/^https?:\/\//i.test(path))
            return path;
        return `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
    }
    async request(path, init = {}) {
        const headers = new Headers(init.headers);
        const request = {
            ...init,
            headers
        };
        if (this.token)
            headers.set('Authorization', `Bearer ${this.token}`);
        if (init.json !== undefined) {
            headers.set('Content-Type', 'application/json');
            request.body = JSON.stringify(init.json);
        }
        delete request.json;
        const url = this.absolute(path);
        this.logger.debug('[SkyChat API] request', { url, method: request.method || 'GET', body: init.json });
        const res = await this.fetcher(url, request);
        const parsed = await res.json().catch(() => ({ ok: false, error: 'Invalid JSON response' }));
        const data = (parsed && typeof parsed === 'object'
            ? parsed
            : { ok: false, error: String(parsed || 'Invalid JSON response') });
        data.status = res.status;
        if (!res.ok && !data.error)
            data.error = `HTTP ${res.status}`;
        this.logger.debug('[SkyChat API] response', { url, status: res.status, data });
        return data;
    }
    idAvailable(id) {
        return this.request('/api/id-available', { method: 'POST', json: { id } });
    }
    register(payload) {
        return this.request('/api/register', { method: 'POST', json: payload });
    }
    login(payload) {
        return this.request('/api/login', { method: 'POST', json: payload });
    }
    me() {
        return this.request('/api/me');
    }
    updateDeviceConnection(payload) {
        return this.request('/api/device-connection', { method: 'POST', json: payload });
    }
    friends() {
        return this.request('/api/friends');
    }
    search(q) {
        return this.request(`/api/search?q=${encodeURIComponent(q)}`);
    }
    getGroups() {
        return this.request('/api/groups');
    }
    createGroup(name, members) {
        return this.request('/api/groups', { method: 'POST', json: { name, members } });
    }
    getGroupMembers(groupId) {
        return this.request(`/api/groups/${encodeURIComponent(groupId)}/members`);
    }
    updateGroup(groupId, payload) {
        return this.request(`/api/groups/${encodeURIComponent(groupId)}`, { method: 'PUT', json: payload });
    }
    addGroupMember(groupId, userId) {
        return this.request(`/api/groups/${encodeURIComponent(groupId)}/members`, { method: 'POST', json: { userId } });
    }
    removeGroupMember(groupId, userId) {
        return this.request(`/api/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`, { method: 'DELETE' });
    }
    changeGroupMemberRole(groupId, userId, role) {
        return this.request(`/api/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}/role`, {
            method: 'PUT',
            json: { role }
        });
    }
    leaveGroup(groupId) {
        return this.request(`/api/groups/${encodeURIComponent(groupId)}/leave`, { method: 'POST' });
    }
    messagesSync(payload) {
        return this.request('/api/messages/sync', { method: 'POST', json: payload });
    }
    messagesHistory(payload) {
        return this.request('/api/messages/history', { method: 'POST', json: payload });
    }
    searchMessages(payload) {
        const params = new URLSearchParams();
        params.set('peerId', payload.peerId);
        if (payload.q)
            params.set('q', payload.q);
        if (payload.date)
            params.set('date', payload.date);
        if (payload.limit)
            params.set('limit', String(payload.limit));
        return this.request(`/api/messages/search?${params.toString()}`);
    }
    listChatFiles(peerId) {
        return this.request(`/api/chat-files?peerId=${encodeURIComponent(peerId)}`);
    }
    deleteChatFile(fileId) {
        return this.request(`/api/chat-files/${encodeURIComponent(fileId)}`, { method: 'DELETE' });
    }
    keyBackup(payload) {
        return this.request('/api/key/backup', { method: 'POST', json: payload });
    }
    keyRestore() {
        return this.request('/api/key/restore');
    }
    keyRestoreByPassphrase(payload) {
        return this.request('/api/key/restore-by-passphrase', { method: 'POST', json: payload });
    }
    keyReset(payload) {
        return this.request('/api/key/reset', { method: 'POST', json: payload });
    }
    getUserConfig() {
        return this.request('/api/user-config');
    }
    updateUserConfig(patch) {
        return this.request('/api/user-config', { method: 'POST', json: { patch } });
    }
    getContactSettings(peerId) {
        const query = peerId ? `?peerId=${encodeURIComponent(peerId)}` : '';
        return this.request(`/api/contact-settings${query}`);
    }
    updateContactSettings(peerId, patch) {
        return this.request(`/api/contact-settings/${encodeURIComponent(peerId)}`, {
            method: 'PUT',
            json: patch
        });
    }
    profileUpdate(payload) {
        return this.request('/api/profile/update', { method: 'POST', json: payload });
    }
    getProfileTimeZone() {
        return this.request('/api/profile/timezone');
    }
    updateProfileTimeZone(payload) {
        return this.request('/api/profile/timezone', { method: 'PUT', json: payload });
    }
    getCustomStatusHistory() {
        return this.request('/api/custom-status/history');
    }
    deleteCustomStatusHistory(id) {
        return this.request(`/api/custom-status/history/${encodeURIComponent(String(id))}`, { method: 'DELETE' });
    }
    weatherUpdate(payload) {
        return this.request('/api/weather', { method: 'POST', json: payload });
    }
    weatherMe() {
        return this.request('/api/weather/me');
    }
    getLinkPreview(url) {
        return this.request(`/api/link-preview?url=${encodeURIComponent(url)}`);
    }
    searchGifs(query) {
        return this.request(`/api/gifs?q=${encodeURIComponent(query)}`);
    }
    getTrendingGifs() {
        return this.request('/api/gifs');
    }
    async uploadTypingAnimation(file) {
        const fd = new FormData();
        fd.append('animation', file);
        const headers = new Headers();
        if (this.token)
            headers.set('Authorization', `Bearer ${this.token}`);
        const res = await this.fetcher(this.absolute('/api/profile/upload-typing-animation'), {
            method: 'POST',
            headers,
            body: fd
        });
        return (await res.json().catch(() => ({ ok: false, error: 'Upload response parse failed' })));
    }
    listGameSessions() {
        return this.request('/api/games');
    }
    getGameSession(gameId) {
        return this.request(`/api/games/${encodeURIComponent(gameId)}`);
    }
    createGameSession(payload) {
        return this.request('/api/games/invite', { method: 'POST', json: payload });
    }
    startSudokuSolo(payload = {}) {
        return this.request('/api/games/sudoku/solo', { method: 'POST', json: payload });
    }
    startSoloGame(payload = {}) {
        return this.request('/api/games/solo', { method: 'POST', json: payload });
    }
    respondGameInvite(gameId, accepted) {
        return this.request(`/api/games/${encodeURIComponent(gameId)}/${accepted ? 'accept' : 'reject'}`, { method: 'POST' });
    }
    readyGameSession(gameId) {
        return this.request(`/api/games/${encodeURIComponent(gameId)}/ready`, { method: 'POST' });
    }
    startGameSession(gameId, payload = {}) {
        return this.request(`/api/games/${encodeURIComponent(gameId)}/start`, { method: 'POST', json: payload });
    }
    abandonGameSession(gameId) {
        return this.request(`/api/games/${encodeURIComponent(gameId)}/abandon`, { method: 'POST' });
    }
    gameAction(gameId, payload) {
        return this.request(`/api/games/${encodeURIComponent(gameId)}/action`, { method: 'POST', json: payload });
    }
    gameMove(gameId, payload) {
        return this.request(`/api/games/${encodeURIComponent(gameId)}/move`, { method: 'POST', json: payload });
    }
    saveGameRecord(gameKey, payload) {
        const key = encodeURIComponent(gameKey);
        if (gameKey === '2048')
            return this.request('/api/games/2048/record', { method: 'POST', json: payload });
        if (gameKey === 'minesweeper')
            return this.request('/api/games/minesweeper/record', { method: 'POST', json: payload });
        return this.request(`/api/games/board/${key}/record`, { method: 'POST', json: payload });
    }
    listWhiteboards(peerId) {
        const suffix = peerId ? `?peerId=${encodeURIComponent(peerId)}` : '';
        return this.request(`/api/whiteboards${suffix}`);
    }
    createWhiteboard(payload) {
        return this.request('/api/whiteboards', { method: 'POST', json: payload });
    }
    saveWhiteboardState(boardId, statePayload) {
        return this.request(`/api/whiteboards/${encodeURIComponent(boardId)}/state`, {
            method: 'POST',
            json: { state: statePayload }
        });
    }
    createMeeting(payload) {
        return this.request('/api/meetings', {
            method: 'POST',
            json: payload
        });
    }
    getMeeting(meetingId) {
        return this.request(`/api/meetings/${encodeURIComponent(String(meetingId))}`);
    }
    respondMeeting(meetingId, accept) {
        return this.request(`/api/meetings/${encodeURIComponent(String(meetingId))}/respond`, {
            method: 'POST',
            json: { accept }
        });
    }
    endMeeting(meetingId, reason = 'ended-by-organizer') {
        return this.request(`/api/meetings/${encodeURIComponent(String(meetingId))}/end`, {
            method: 'POST',
            json: { reason }
        });
    }
    deviceTransferIceServers() {
        return this.request('/api/turn-credentials');
    }
    getVapidKey() {
        return this.request('/api/vapid-key');
    }
    subscribe(subscription) {
        return this.request('/api/subscribe', { method: 'POST', json: { subscription } });
    }
    unsubscribe(payload) {
        return this.request('/api/unsubscribe', { method: 'POST', json: payload });
    }
    getTurnCredentials() {
        return this.request('/api/turn-credentials');
    }
    async upload(file, filename = 'file', originalMimeType = '') {
        const fd = new FormData();
        fd.append('file', file, filename);
        if (originalMimeType)
            fd.append('originalMimeType', originalMimeType);
        const headers = new Headers();
        if (this.token)
            headers.set('Authorization', `Bearer ${this.token}`);
        const res = await this.fetcher(this.absolute('/api/upload'), {
            method: 'POST',
            headers,
            body: fd
        });
        return (await res.json().catch(() => ({ ok: false, error: 'Upload response parse failed' })));
    }
}
//# sourceMappingURL=api.js.map